import { runAz } from "./az.js";
import { INSTALLER_GRANT_DESCRIPTION, GATEWAY_SUBSCOPE_GRANT_DESCRIPTION } from "./bootstrap-mi.js";

const ROLE_ASSIGNMENT_API = "2022-04-01";

// The gateway MI's two subscription-scoped reader roles (role-assignments-subscription.bicep).
const GATEWAY_SUBSCOPE_ROLES = ["Cost Management Reader", "Monitoring Reader"];

interface RoleAssignmentRow {
  id: string;
  principalName?: string;
  principalType?: string;
  roleDefinitionName?: string;
  scope?: string;
  description?: string;
  createdOn?: string;
}

async function listSubscriptionAssignments(subscriptionId: string): Promise<RoleAssignmentRow[]> {
  return JSON.parse(
    (await runAz([
      "role", "assignment", "list", "--all", "--subscription", subscriptionId, "-o", "json",
    ]).catch(() => "[]")) || "[]",
  ) as RoleAssignmentRow[];
}

async function deleteAssignmentsById(ids: string[]): Promise<void> {
  for (const id of ids) {
    await runAz([
      "rest", "--method", "delete",
      "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`,
    ]).catch(() => undefined);
  }
}

/**
 * Find and optionally delete ORPHANED gateway-MI subscription-scoped reader roles
 * (Cost Management Reader + Monitoring Reader). Unlike the installer Owner sweep,
 * the gateway MI is PERMANENT, so a live platform's grants must never be swept —
 * `!principalName` (the principal is gone) is the required orphan gate. A match is:
 *   - no principalName (principal deleted), ServicePrincipal-typed, exactly sub scope, AND
 *     - description is the gateway tag (precise — future grants), OR
 *     - the role is one of the two gateway sub-scope readers (generic — pre-tag legacy orphans).
 * Deletes only when execute=true. Returns all matched candidates regardless.
 */
export async function sweepOrphanGatewaySubRoles(opts: {
  subscriptionId: string;
  execute: boolean;
}): Promise<{ id: string; description?: string; roleDefinitionName?: string; createdOn?: string }[]> {
  const subScope = `/subscriptions/${opts.subscriptionId}`;
  const all = await listSubscriptionAssignments(opts.subscriptionId);
  const matched = all.filter((r) => {
    if (r.principalName) return false; // live principal — never sweep a running platform's grants
    if (r.principalType !== "ServicePrincipal") return false;
    if (r.scope !== subScope) return false;
    if (r.description === GATEWAY_SUBSCOPE_GRANT_DESCRIPTION) return true;
    return GATEWAY_SUBSCOPE_ROLES.includes(r.roleDefinitionName ?? "");
  });
  if (opts.execute) await deleteAssignmentsById(matched.map((r) => r.id));
  return matched.map((r) => ({ id: r.id, description: r.description, roleDefinitionName: r.roleDefinitionName, createdOn: r.createdOn }));
}

/**
 * Find and optionally delete orphaned installer Owner@subscription role assignments.
 * An orphan matches if:
 *   - Its description is the installer tag (precise), OR
 *   - It has no principalName, is ServicePrincipal-typed, Owner role, exactly sub scope (generic).
 * Deletes only when execute=true. Returns all matched candidates regardless.
 */
export async function sweepOrphanOwnerAssignments(opts: {
  subscriptionId: string;
  execute: boolean;
}): Promise<{ id: string; description?: string; createdOn?: string }[]> {
  const subScope = `/subscriptions/${opts.subscriptionId}`;
  const all = await listSubscriptionAssignments(opts.subscriptionId);
  const matched = all.filter((r) => {
    if (r.description === INSTALLER_GRANT_DESCRIPTION) return true;
    return (
      !r.principalName &&
      r.principalType === "ServicePrincipal" &&
      r.roleDefinitionName === "Owner" &&
      r.scope === subScope
    );
  });
  if (opts.execute) await deleteAssignmentsById(matched.map((r) => r.id));
  return matched.map((r) => ({ id: r.id, description: r.description, createdOn: r.createdOn }));
}

/**
 * Tear down the installer scaffolding: ACI → MI → the MI's
 * role assignments. Assignments are deleted BY ARM ID via the REST API —
 * the robust method when the principal may already be gone
 * (`--assignee`/`--ids`/`--name` all fail for a deleted principal). The
 * platform RG and the gateway's own assignments persist.
 */
export async function reapInstaller(opts: {
  subscriptionId: string;
  resourceGroup: string;
  aciName: string;
  miName: string;
  roleAssignmentIds: string[];
  principalId: string;
}): Promise<void> {
  const swallow = async (p: Promise<unknown>): Promise<void> => { try { await p; } catch { /* idempotent */ } };

  await swallow(runAz(["container", "delete", "--name", opts.aciName, "--resource-group", opts.resourceGroup, "--subscription", opts.subscriptionId, "--yes", "--only-show-errors"]));
  await swallow(runAz(["identity", "delete", "--name", opts.miName, "--resource-group", opts.resourceGroup, "--subscription", opts.subscriptionId]));
  for (const id of opts.roleAssignmentIds) {
    await swallow(runAz([
      "rest", "--method", "delete",
      "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`,
    ]));
  }

  // Belt-and-suspenders: a deleted MI's sub-scope Owner can survive if the captured
  // id list was stale/empty. The assignment record keeps principalId after deletion.
  const survivors = JSON.parse(
    (await runAz([
      "role", "assignment", "list", "--all", "--subscription", opts.subscriptionId,
      "--query", `[?principalId=='${opts.principalId}' && roleDefinitionName=='Owner'].id`, "-o", "json",
    ]).catch(() => "[]")) || "[]",
  ) as string[];
  for (const id of survivors) {
    await swallow(runAz(["rest", "--method", "delete", "--url", `https://management.azure.com${id}?api-version=${ROLE_ASSIGNMENT_API}`]));
  }
}
